import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import AppShell from "@/components/layout/AppShell";
import { HelpLang, useHelpLang } from "@/components/help/HelpLang";
import { RequestRow, Thread } from "@/components/area/SupportRequests";
import { useCategories } from "@/components/area/SupportAttachments";

const STATUSES = ["active", "all", "open", "in_progress", "waiting_client", "done", "closed"];
const STAFF = { can_create: true, is_staff: true, limits: { files: 3 }, statuses: { open: 1, in_progress: 1, waiting_client: 1, done: 1, closed: 1 } };

// MUSORA staff inbox: every client's requests in one list; each thread is answered through ITS tenant's endpoints (isolation stays server-side).
export default function SupportInbox() {
  const api = useApi();
  const { t, lang } = useHelpLang();
  const cats = useCategories(lang);
  const [f, setF] = useState({ status: "active", category: "", client: "" });
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/support-requests", { params: { status: f.status, category: f.category || undefined } }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), [api, f.status, f.category]);
  useEffect(() => { load(); }, [load]);
  const shown = (rows || []).filter((r) => !f.client || `${r.tenant_name} ${r.tenant_id}`.toLowerCase().includes(f.client.toLowerCase()));
  const label = (s) => (s === "active" ? t("staff.status_active") : s === "all" ? t("staff.status_all") : t(s === "waiting_client" ? "req.waiting_client_staff" : `req.status.${s}`));
  return (
    <AppShell>
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-8" lang={lang} data-testid="support-inbox">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h1 className="text-2xl font-semibold text-slate-900">{t("staff.title")}</h1><p className="mt-1 text-sm text-slate-600">{t("staff.subtitle")}</p></div>
        <HelpLang lang={lang} /></div>
      <div className="grid gap-2 sm:grid-cols-3">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="inbox-status">{STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select>
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="inbox-category">
          <option value="">{t("staff.category_all")}</option>{Object.entries(cats).map(([k, l]) => <option key={k} value={k}>{l}</option>)}<option value="other">{t("cat_other")}</option></select>
        <Input value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} placeholder={t("staff.client_filter")} className="h-11" data-testid="inbox-client" />
      </div>
      {!rows ? <Loader2 className="animate-spin text-slate-400" /> : shown.length === 0 ? <p className="text-sm text-slate-500" data-testid="inbox-empty">{t("staff.empty")}</p>
        : shown.map((r) => (
          <RequestRow key={r.id} r={r} open={open} setOpen={setOpen} cats={cats} staff extra={<b className="mr-1 font-medium text-slate-700" data-testid={`inbox-client-${r.id}`}>{r.tenant_name || r.tenant_id} ·</b>}>
            <Link to={`/crm/${r.tenant_id}`} className="text-xs text-sky-700 hover:underline" data-testid={`inbox-open-client-${r.id}`}>{t("staff.open_client")}</Link>
            <Thread tid={r.tenant_id} r={r} d={STAFF} onChange={load} />
          </RequestRow>))}
    </div>
    </AppShell>);
}
